export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Placed<T> extends Rect {
  item: T;
}

/** The worst aspect ratio of a row of areas laid along a side of this length. */
export function worst(row: readonly number[], side: number): number {
  if (row.length === 0 || side <= 0) return Infinity;
  const sum = row.reduce((a, b) => a + b, 0);
  const max = Math.max(...row);
  const min = Math.min(...row);
  if (min <= 0) return Infinity;
  return Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min));
}

/** A squarified treemap (Bruls, Huizing, van Wijk): the area of each rectangle is its share of the total. */
export function squarify<T>(items: readonly T[], value: (item: T) => number, rect: Rect): Placed<T>[] {
  const kept = items.filter((item) => value(item) > 0).sort((a, b) => value(b) - value(a));
  const total = kept.reduce((sum, item) => sum + value(item), 0);
  if (total <= 0 || rect.w <= 0 || rect.h <= 0) return [];
  const scale = (rect.w * rect.h) / total;
  const areas = kept.map((item) => value(item) * scale);
  const placed: Placed<T>[] = [];
  let { x, y, w, h } = rect;
  let row: number[] = [];

  const lay = () => {
    const sum = row.reduce((a, n) => a + areas[n], 0);
    if (w >= h) {
      const width = h > 0 ? sum / h : 0;
      let top = y;
      for (const n of row) {
        const height = width > 0 ? areas[n] / width : 0;
        placed.push({ item: kept[n], x, y: top, w: width, h: height });
        top += height;
      }
      x += width;
      w = Math.max(0, w - width);
    } else {
      const height = w > 0 ? sum / w : 0;
      let left = x;
      for (const n of row) {
        const width = height > 0 ? areas[n] / height : 0;
        placed.push({ item: kept[n], x: left, y, w: width, h: height });
        left += width;
      }
      y += height;
      h = Math.max(0, h - height);
    }
    row = [];
  };

  let n = 0;
  while (n < areas.length) {
    const side = Math.min(w, h);
    const now = row.map((k) => areas[k]);
    if (row.length === 0 || worst([...now, areas[n]], side) <= worst(now, side)) {
      row.push(n);
      n += 1;
    } else {
      lay();
    }
  }
  if (row.length) lay();
  return placed;
}

/** The rectangle inside a border of `pad` on each side, and a `top` strip for a label. */
export function inset(rect: Rect, pad: number, top = 0): Rect {
  const w = Math.max(0, rect.w - 2 * pad);
  const h = Math.max(0, rect.h - 2 * pad - top);
  return { x: rect.x + pad, y: rect.y + pad + top, w, h };
}
